import { memo, useEffect, useState } from "react";
import { IntlProvider } from "react-intl";
import { BrowserRouter as Router } from "react-router-dom";

import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";

import { AppUIProviders } from "../context";
import { useAuth } from "../context/auth";
import { useUser } from "../context/user";
import { messages, dateFnsLocales } from "../langs";
import { Routes, routes } from "../routes";
import theme from "../styles/theme";

const { palette } = theme;

declare const zE: any;

enum ScriptStatus {
  Loading = "LOADING",
  Ready = "READY",
  Error = "ERROR",
}

const App = (): JSX.Element => {
  const { isAuthenticated } = useAuth();
  const { locale } = useUser();
  const [scriptStatus, setScriptStatus] = useState<ScriptStatus>(ScriptStatus.Loading);

  useEffect(() => {
    const onScriptLoaded = ({ type }: Event) => {
      if (type === "load") {
        setScriptStatus(ScriptStatus.Ready);
      }
    };
    const zendeskScript = document.createElement("script");
    if (isAuthenticated && !document.getElementById("ze-snippet")) {
      // @ts-ignore
      window.zESettings = {
        webWidget: {
          color: {
            theme: palette.primary.main,
          },
        },
      };
      zendeskScript.addEventListener("load", onScriptLoaded);
      zendeskScript.id = "ze-snippet";
      zendeskScript.src = "https://static.zdassets.com/ekr/snippet.js?key=7f910e1b-9089-44e7-83f2-4cf1d112cdfa";
      document.head.appendChild(zendeskScript);
    }

    const zendeskWedgets = document.getElementById("launcher")?.parentElement;
    if (zendeskWedgets) zendeskWedgets.style.display = isAuthenticated ? "block" : "none";
    return () => {
      zendeskScript.removeEventListener("load", onScriptLoaded);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (scriptStatus === ScriptStatus.Ready) {
      if (zE) {
        const getLocale = () => {
          switch (locale) {
            case "zh":
              return "zh-tw";
            case "ja":
              return "ja";
            case "en":
            default:
              return "en-US";
          }
        };

        zE("webWidget", "setLocale", getLocale());
      }
    }
  }, [locale, scriptStatus]);

  return (
    <IntlProvider locale={locale} messages={messages[locale]}>
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={dateFnsLocales[locale]}>
        <Router
          getUserConfirmation={() => {
            /* Empty callback to block the default browser prompt */
          }}
        >
          <AppUIProviders>
            <Routes routes={routes} />
          </AppUIProviders>
        </Router>
      </MuiPickersUtilsProvider>
    </IntlProvider>
  );
};

export default memo(App);
